/**
 * Hydrogen Error Handling
 *
 * Provides helpful error messages for common mistakes when using the Hydrogen framework.
 * Errors include expected vs received values and documentation links.
 *
 * @module errors
 */
import { logRuntimeWarn } from './runtime-logger.js';
// Track warnings that have already been shown to avoid spam
const shownWarnings = new Set();
// Private documentation reference used in framework error messages.
const DOCS_BASE = 'internal Hydrogen docs';
/**
 * Custom error class for Hydrogen framework errors.
 * Provides structured error messages with expected/received values and doc links.
 */
export class HydrogenError extends Error {
    type;
    componentName;
    expected;
    received;
    docPath;
    constructor(type, message, options = {}) {
        const { expected, received, docPath, componentName } = options;
        // Build the full error message
        let fullMessage = `[Hydrogen] ${type}`;
        if (componentName) {
            fullMessage += `: ${componentName}`;
        }
        fullMessage += ` ${message}`;
        // Add expected/received if provided
        if (expected !== undefined || received !== undefined) {
            fullMessage += '\n';
            if (expected !== undefined) {
                fullMessage += `\nExpected: ${formatValue(expected)}`;
            }
            if (received !== undefined) {
                fullMessage += `\nReceived: ${formatValue(received)}`;
            }
        }
        // Add documentation reference
        if (docPath) {
            fullMessage += `\n\nSee: ${DOCS_BASE}: ${docPath}`;
        }
        super(fullMessage);
        this.name = 'HydrogenError';
        this.type = type;
        this.componentName = componentName;
        this.expected = expected;
        this.received = received;
        this.docPath = docPath;
    }
}
/**
 * Formats a value for display in error messages.
 * Handles objects, functions, and primitives.
 *
 */
function formatValue(value) {
    if (value === undefined)
        return 'undefined';
    if (value === null)
        return 'null';
    if (typeof value === 'function')
        return 'function';
    if (typeof value === 'string')
        return `"${value}"`;
    if (typeof value === 'object') {
        // For objects, show a condensed representation
        if (Array.isArray(value)) {
            return `Array(${value.length})`;
        }
        try {
            const keys = Object.keys(value);
            if (keys.length === 0)
                return '{}';
            if (keys.length <= 3) {
                return `{ ${keys.join(', ')} }`;
            }
            return `{ ${keys.slice(0, 3).join(', ')}, ... }`;
        }
        catch {
            return '[object]';
        }
    }
    return String(value);
}
/**
 * Validates that a component returns the correct structure.
 * Components must return an html string and may provide mount and unmount functions.
 *
 * @throws {HydrogenError} If the component result is invalid
 */
export function validateComponent(result, name = 'Component') {
    // Check for missing result
    if (result === undefined || result === null) {
        throw new HydrogenError('ComponentError', 'did not return {html: string}', {
            componentName: name,
            expected: ["{ html: '<div>...</div>', ", 'mount?: fn, ', 'unmount?: fn }'].join(''), // ubs:ignore -- #322: syntax is not a nested conditional expression
            received: result,
            docPath: 'components',
        });
    }
    // Check for non-object result
    if (typeof result !== 'object') {
        throw new HydrogenError('ComponentError', 'must return an object', {
            componentName: name,
            expected: ["{ html: '<div>...</div>', ", 'mount?: fn, ', 'unmount?: fn }'].join(''), // ubs:ignore -- #322: syntax is not a nested conditional expression
            received: result,
            docPath: 'components',
        });
    }
    // Check for missing html property
    const component = result;
    if (!('html' in component)) {
        throw new HydrogenError('ComponentError', 'must include an html property', {
            componentName: name,
            expected: ["{ html: '<div>...</div>', ", 'mount?: fn, ', 'unmount?: fn }'].join(''), // ubs:ignore -- #322: syntax is not a nested conditional expression
            received: result,
            docPath: 'components',
        });
    }
    // Check that html is a string
    if (typeof component.html !== 'string') {
        throw new HydrogenError('ComponentError', 'html property must be a string', {
            componentName: name,
            expected: "html: '<div>...</div>'",
            received: `html: ${formatValue(component.html)}`,
            docPath: 'components',
        });
    }
    // Validate optional mount function
    if ('mount' in component && typeof component.mount !== 'function') {
        throw new HydrogenError('ComponentError', 'mount must be a function', {
            componentName: name,
            expected: 'mount: function(element) { ... }',
            received: `mount: ${formatValue(component.mount)}`,
            docPath: 'components#lifecycle',
        });
    }
    // Validate optional unmount function
    if ('unmount' in component && typeof component.unmount !== 'function') {
        throw new HydrogenError('ComponentError', 'unmount must be a function', {
            componentName: name,
            expected: 'unmount: function() { ... }',
            received: `unmount: ${formatValue(component.unmount)}`,
            docPath: 'components#lifecycle',
        });
    }
}
/**
 * Validates route registration parameters.
 *
 * @throws {HydrogenError} If the route parameters are invalid
 */
export function validateRoute(path, component) {
    // Validate path
    if (typeof path !== 'string') {
        throw new HydrogenError('RouterError', 'route path must be a string', {
            expected: "'/path' or '/path/*'",
            received: path,
            docPath: 'routing',
        });
    }
    if (path.length === 0) {
        throw new HydrogenError('RouterError', 'route path cannot be empty', {
            expected: "'/' or '/path'",
            received: "''",
            docPath: 'routing',
        });
    }
    // Validate component function
    if (typeof component !== 'function') {
        throw new HydrogenError('RouterError', 'route component must be a function', {
            expected: '() => ({ html: "..." })',
            received: component,
            docPath: 'routing',
        });
    }
}
/**
 * Validates that a callback function is provided to subscribe.
 *
 * @throws {HydrogenError} If the callback is not a function
 */
export function validateSubscribeCallback(callback) {
    if (typeof callback !== 'function') {
        throw new HydrogenError('StoreError', 'subscribe requires a callback function', {
            expected: 'subscribe(() => { ... }) or subscribe([keys], () => { ... })',
            received: callback,
            docPath: 'state-management#subscribe',
        });
    }
}
/**
 * Validates that an element is provided for mounting.
 *
 * @throws {HydrogenError} If the element is null or undefined
 */
export function validateMountElement(element, context = 'mount') {
    if (element === null || element === undefined) {
        throw new HydrogenError('MountError', `${context} requires a valid DOM element`, {
            expected: 'HTMLElement (e.g., document.getElementById("app"))',
            received: element,
            docPath: 'getting-started#mounting',
        });
    }
    // Check if it looks like a DOM element (has innerHTML property)
    if (typeof element !== 'object' || !('innerHTML' in element)) {
        throw new HydrogenError('MountError', `${context} requires a DOM element`, {
            expected: 'HTMLElement',
            received: element,
            docPath: 'getting-started#mounting',
        });
    }
}
/**
 * Logs a warning message once per session.
 * Subsequent calls with the same id will be ignored.
 *
 */
export function warnOnce(id, message) {
    if (shownWarnings.has(id)) {
        return;
    }
    shownWarnings.add(id);
    logRuntimeWarn('hydrogen.warning', message);
}
/**
 * Clears all tracked warnings.
 * Useful for testing or when warnings should be shown again.
 */
export function clearWarnings() {
    shownWarnings.clear();
}
//# sourceMappingURL=errors.js.map